/**
 * Der Ton-Leitstand — eine Wahrheit darüber, was gerade klingt.
 * (04.09.2026)
 *
 * ## Der Anlass
 *
 * Martin: *„Wenn ich mich auf meinen Alltag konzentriere, habe ich viele
 * Player da drinnen und man weiß gar nicht mehr, wo welcher läuft. Und das
 * ist oft inkonsistent: Man hört zwar eine Stimme, aber der Player läuft
 * offensichtlich nicht."*
 *
 * ## Was gemessen wurde
 *
 * Im Frontend erzeugen **26 Stellen** Ton, verteilt auf **fünf unabhängige
 * Wege** (Spur-Player, Vorlesen, Erzählstimme, Rückmeldetöne, Anruf).
 * Stellen, an denen einer vom anderen weiß: **null**.
 *
 * Die Idee einer Absprache gab es sogar schon — zweimal, und beide Male nur
 * innerhalb eines Moduls:
 *
 *     audio-player.ts      let currentlyPlaying        (Player ↔ Player)
 *     narration-engine.ts  let activeNarrationEngine   (Vorleser ↔ Vorleser)
 *
 * Zwei Ringe, die einander nicht kennen. Deshalb ist die Inkonsistenz kein
 * Fehler, den man findet: Beide Seiten arbeiten korrekt, nur aneinander
 * vorbei. Solange die Schicht fehlt, die sie verbindet, ist jede Reparatur
 * ein Zufallstreffer.
 *
 * ## Die Bühne
 *
 * Es gibt genau **einen** Platz für Ton, der Aufmerksamkeit beansprucht: eine
 * Spur, eine vorgelesene Antwort, Musik, ein Anruf. Wer ihn betritt,
 * verdrängt den vorherigen — freundlich, über dessen eigenes `anhalten`.
 *
 * ⚠️ **Kurze Geräusche gehören NICHT hierher.** Ein Klick-Ton beim Antippen
 * darf das Vorlesen nicht abwürgen. Sie laufen weiter direkt über
 * `audio-feedback.ts` und tauchen im Leitstand nie auf. Diese Unterscheidung
 * ist der Grund, warum die Regel „nur einer darf klingen" hier nicht absolut
 * gilt — ohne sie hätte man sich den nächsten Widerspruch eingebaut.
 */

/** Was für ein Ton die Bühne beansprucht. Bestimmt auch das Symbol. */
export type TonArt = "spur" | "vorlesen" | "musik" | "anruf";

/** Wie weit ein Ton ist — soweit die Quelle es überhaupt weiß. */
interface TonFortschritt {
  /** Sekunden seit Beginn. */
  position: number;
  /** Gesamtlänge in Sekunden, oder 0 wenn unbekannt (Strom). */
  dauer: number;
  /** `true`: die Dauer ist aus der Zeichenzahl GESCHÄTZT, nicht gemessen (lange-spur.ts wertet sie nicht als Ende). */
  geschaetzt?: boolean;
}

/** Was eine Quelle beim Betreten der Bühne über sich sagt. */
interface TonQuelle {
  art: TonArt;
  /** Was gerade klingt, in Martins Worten — nicht der Dateiname. */
  titel: string;
  /** Wird gerufen, wenn ein anderer die Bühne bekommt. MUSS still werden. */
  anhalten: () => void;
  /** Fortsetzen, falls die Quelle das kann (sonst zeigt die Leiste kein Play). */
  fortsetzen?: () => void;
  /** Aktueller Stand, falls die Quelle ihn kennt. */
  fortschritt?: () => TonFortschritt | null;
  /** Springen, falls die Quelle das kann. */
  springe?: (sekunden: number) => void;
  /**
   * Die ROHE Abspielzeit (Sekunden Ton, ohne Wanduhr-Zuschlag). Rückt sie bei „klingt“ nicht vor, steht die Wiedergabe
   * wirklich (lange-spur.ts, Hänge-Freigabe). `fortschritt().position` taugt dafür nicht: die Engine zählt bei stehender
   * Uhr die Wanduhr dazu. Fehlt die Angabe, gilt die Position.
   */
  abspielzeit?: () => number;
  /**
   * Ganz beenden: still werden UND die Bühne räumen. (05.09.2026)
   *
   * Martin: *„Den Player in der Fußzeile kann man nicht auf Stopp drücken
   * und man kann ihn auch nicht schließen. Ist ja aber wichtig — Pause,
   * Stopp und Schließen."*
   *
   * ⚠️ Unterschied zu `anhalten`: Pause hält die Stelle, Stopp gibt sie auf.
   *    Wer nur `anhalten` ruft und die Bühne räumt, lässt eine Quelle
   *    zurück, die sich für pausiert hält — beim nächsten Betreten spielt
   *    sie an der alten Stelle weiter.
   *
   * Fehlt die Angabe, tut `beende()` das Nächstbeste: anhalten und räumen.
   */
  beenden?: () => void;
}

/** Was die Oberfläche zu sehen bekommt. */
export interface TonStand {
  art: TonArt;
  titel: string;
  /** Klingt es gerade, oder ist es angehalten? */
  klingt: boolean;
  fortschritt: TonFortschritt | null;
  /** Die rohe Abspielzeit (s), falls die Quelle sie kennt. */
  abspielzeit: number | null;
  /** Kann diese Quelle fortgesetzt werden? */
  fortsetzbar: boolean;
  /** Kann in dieser Quelle gesprungen werden? */
  springbar: boolean;
}

/** Der Platz auf der Bühne, den eine Quelle hält. */
export interface Buehnenplatz {
  /** Melden, dass gerade geklungen wird (oder eben nicht). */
  melde(klingt: boolean): void;
  /** Bühne freigeben. Danach ist der Platz wertlos. */
  verlasse(): void;
  /** Hält diese Quelle den Platz noch? */
  readonly gilt: boolean;
}

interface Belegung {
  quelle: TonQuelle;
  klingt: boolean;
}

let belegung: Belegung | null = null;
const beobachter = new Set<(stand: TonStand | null) => void>();

function standJetzt(): TonStand | null {
  if (!belegung) return null;
  const q = belegung.quelle;
  let fortschritt: TonFortschritt | null = null;
  try {
    fortschritt = q.fortschritt?.() ?? null;
  } catch {
    // Eine Quelle, die beim Ausrechnen stolpert, darf die Leiste nicht
    // mitreißen. Dann eben ohne Zeitangabe.
    fortschritt = null;
  }
  let abspielzeit: number | null = null;
  try {
    const z = q.abspielzeit?.();
    abspielzeit = typeof z === "number" && Number.isFinite(z) ? z : null;
  } catch {
    abspielzeit = null;
  }
  return {
    art: q.art,
    titel: q.titel,
    klingt: belegung.klingt,
    fortschritt,
    abspielzeit,
    fortsetzbar: typeof q.fortsetzen === "function",
    springbar: typeof q.springe === "function",
  };
}

function sageBescheid(): void {
  const stand = standJetzt();
  for (const fn of [...beobachter]) {
    try {
      fn(stand);
    } catch {
      // Ein kaputter Beobachter darf die anderen nicht blockieren.
    }
  }
}

/**
 * Bühne betreten. Der bisherige Belegung wird angehalten — über ihr eigenes
 * `anhalten`, nicht mit Gewalt.
 *
 * ⚠️ `anhalten` der verdrängten Quelle läuft, BEVOR die neue eingetragen ist.
 * Ruft sie dabei ihrerseits `verlasse()`, trifft das noch den alten Platz und
 * nicht den neuen — sonst würde sich die neue Quelle selbst abmelden.
 */
export function betritt(quelle: TonQuelle): Buehnenplatz {
  const vorher = belegung;
  if (vorher && vorher.quelle !== quelle) {
    belegung = null;
    try {
      vorher.quelle.anhalten();
    } catch {
      // Eine Quelle, die sich nicht anhalten lässt, verliert trotzdem den
      // Platz. Alles andere hieße: ein Fehler blockiert die Bühne für immer.
    }
  }
  const meine: Belegung = { quelle, klingt: true };
  belegung = meine;
  sageBescheid();

  return {
    get gilt() {
      return belegung === meine;
    },
    melde(klingt: boolean) {
      if (belegung !== meine || meine.klingt === klingt) return;
      meine.klingt = klingt;
      sageBescheid();
    },
    verlasse() {
      if (belegung !== meine) return;
      belegung = null;
      sageBescheid();
    },
  };
}

/** Was gerade klingt — oder null. */
export function aktuellerTon(): TonStand | null {
  return standJetzt();
}

/**
 * Bei Änderungen benachrichtigt werden. Gibt die Abmeldung zurück.
 *
 * Wird sofort einmal mit dem aktuellen Stand gerufen, damit ein neu
 * aufgebautes Stück Oberfläche nicht erst auf das nächste Ereignis warten
 * muss (die Leiste erscheint sonst bei einem Seitenwechsel leer, obwohl
 * längst etwas läuft).
 */
export function beobachte(fn: (stand: TonStand | null) => void): () => void {
  beobachter.add(fn);
  try {
    fn(standJetzt());
  } catch {
    /* siehe sageBescheid */
  }
  return () => {
    beobachter.delete(fn);
  };
}

/** Anhalten, was gerade klingt. Für die Leiste und für „alles still". */
export function halteAn(): void {
  if (!belegung?.klingt) return;
  try {
    belegung.quelle.anhalten();
  } catch {
    /* siehe betritt */
  }
  // Die Quelle meldet den Halt normalerweise selbst über `melde(false)`.
  // Tut sie es nicht, stimmt die Anzeige trotzdem.
  if (belegung) {
    belegung.klingt = false;
    sageBescheid();
  }
}

/** Fortsetzen, wenn die Quelle es kann. */
export function setzeFort(): void {
  const q = belegung?.quelle;
  if (!q?.fortsetzen) return;
  try {
    q.fortsetzen();
  } catch {
    /* siehe betritt */
  }
}

/**
 * Alles beenden — für den Stopp-Knopf der Leiste.
 *
 * ⚠️ Räumt die Bühne IMMER, auch wenn die Quelle beim Beenden stolpert.
 *    Ein Stopp, der die Leiste stehen lässt, ist kein Stopp.
 */
export function beende(): void {
  const b = belegung;
  if (!b) return;
  try {
    if (b.quelle.beenden) b.quelle.beenden();
    else b.quelle.anhalten();
  } catch {
    /* siehe betritt */
  }
  if (belegung === b) {
    belegung = null;
    sageBescheid();
  }
}

/** Springen, wenn die Quelle es kann. */
export function springe(sekunden: number): void {
  const q = belegung?.quelle;
  if (!q?.springe) return;
  try {
    q.springe(sekunden);
  } catch {
    /* siehe betritt */
  }
}

/** Nur Tests: Bühne räumen, ohne jemanden zu benachrichtigen. */
export function _leereBuehneFuerTests(): void {
  belegung = null;
  beobachter.clear();
}
